import { router, useFocusEffect } from 'expo-router';
import { useCallback } from 'react';

import { usePermissions } from '@/hooks/use-permissions';

/**
 * Hold a screen until the capability flags have loaded, then send an unentitled user home.
 *
 * ── WHY A HOOK AND NOT AN `if` IN EACH SCREEN ──
 *
 * The naive guard is three lines and two of them are wrong:
 *
 *     if (!permissions.canAccessAdmin) { router.replace('/'); return null; }   // DON'T
 *
 * It fails for exactly the users a capability is FOR. `canViewUsers`, `hasAdminCapability` and
 * friends resolve from `/me/feature-flags`, which is async, so during the first render they are
 * false for a fully-entitled holder — and this guard has already bounced them to the dashboard
 * by the time the answer arrives. A ROLE is synchronous, which is why the Admin role never
 * noticed and the bug reached a user instead of a reviewer. Redirecting during render is also
 * what produced React's "cannot update a component while rendering a different component"
 * warning. `app/users/index.tsx` worked this out first; this is that pattern, factored out so
 * the next screen inherits it rather than rediscovering it.
 *
 * ── REDIRECT ON EVERY FOCUS, NOT ON MOUNT ──
 *
 * The drawer keeps a visited screen MOUNTED. A plain `useEffect([denied])` therefore fired once,
 * on the cold load, and a second in-app visit found `denied` unchanged — no redirect, and a blank
 * page because this hook still returns `true` (#658). `useFocusEffect` re-runs the callback each
 * time the screen regains focus, and again whenever `denied` flips while it is focused (the
 * flags-finished-loading case), without ever firing for a hidden screen.
 *
 * @param allowed - Read from `usePermissions()`. Pass the permission this SCREEN needs, not the
 *   one its section needs: a route layout that admits a group must not be the only thing
 *   standing between a visitor and the most destructive page inside it.
 * @returns `true` while the screen must render nothing — still loading, or denied and leaving.
 */
export function useRouteGuard(allowed: boolean): boolean {
	const { isLoading } = usePermissions();
	const denied = !isLoading && !allowed;

	useFocusEffect(useCallback(() => {
		if (denied) router.replace('/');
	}, [denied]));

	return isLoading || denied;
}
